import * as Intercom from '@intercom/messenger-js-sdk';
import type {
  InitType,
  IntercomSettings,
  UserArgs,
} from '@intercom/messenger-js-sdk/dist/types';

const { default: init, ...methods } = Intercom;

export let appId: string | null = null;
export let hasInitialized = false;
export let isOpen = false;
export let isUserEmailSupplied = false;

const setInitializeWithAppId = (app_id: string) => {
  appId = app_id;
  hasInitialized = true;
};

const attachStateHandlers = () => {
  methods.onShow(() => {
    isOpen = true;
  });
  methods.onHide(() => {
    isOpen = false;
  });
  methods.onUserEmailSupplied(() => {
    isUserEmailSupplied = true;
  });
};

function globalIntercom(command: string, ...args: unknown[]) {
  if (!window.Intercom) {
    console.warn(
      `Could not run Intercom command: ${command}. Intercom is not initialized`,
    );
    return;
  }
  return window.Intercom(command, ...args);
}

/**
 * This method initializes the Intercom application and hook into the events to track
 * the state of the messenger.
 * @param {InitType} params
 */
export const initIntercom = ({ app_id, ...args }: InitType) => {
  if (hasInitialized) {
    return;
  }
  init({ app_id, hide_default_launcher: false, ...args });
  setInitializeWithAppId(app_id);
  attachStateHandlers();

  const queryStringParameters = new URLSearchParams(window.location.search);
  if (queryStringParameters.get('intercom') === 'true') {
    show();
  }
};

/**
 * If you'd like to control when Intercom is loaded, you can use the 'boot'
 * method. This is useful in situations like a one-page Javascript based application
 * where the user may not be logged in when the page loads. You call this method with
 * the standard intercomSettings object.
 * @param params
 */
export const boot = (params: Partial<IntercomSettings> = {}) => {
  if (!hasInitialized) {
    return;
  }
  globalIntercom('boot', {
    hide_default_launcher: false,
    ...params,
    app_id: params.app_id || appId,
  });
};

/**
 * If you have the Inbox product (combined with another product like Messages)
 * you should call the Intercom shutdown method to clear your users’ conversations
 * anytime they logout of your application. Otherwise, the cookie we use to track who was
 * most recently logged in on a given device or computer will keep these conversations
 * in the Messenger for one week. This method will effectively clear out any user
 * data that you have been passing through the JS API.
 */
export const shutdown = () => globalIntercom('shutdown');

/**
 * Calling the update method with some new user data will trigger the JavaScript
 * to look for new messages that should be displayed to the current user (the one
 * whose details are in the window.intercomSettings variable) and show them if they
 * exist.
 *
 * Calling the update method with a JSON object of user details will update
 * those fields on the current user in addition to logging an impression at the current
 * URL and looking for new messages for the user.
 *
 * If the person's ID is not found in the user list when calling the update, a
 * new user will be created.
 *
 * If trying to update a custom_attribute, you do not need
 * to include those values as an object. Include them in-line with the standard
 * Intercom fields and they will be applied accordingly.
 *
 * You can call Intercom('update') without getting throttled up to 20 times per
 * 30 minutes. After the 20th call, you'll be throttled and the quota of 20 calls
 * will get reset every 30 minutes. Reloading the page will refresh this state.
 * @param params
 */
export const update = (params?: UserArgs) => globalIntercom('update', params);

/**
 * This will hide the main Messenger panel if it is open. It will not hide the
 * Messenger Launcher.
 */
export const hide = () => globalIntercom('hide');

/**
 * This will show the Messenger. If there are no new conversations, it will open
 * to the Messenger Home. If there are, it will open with the message list.
 */
export const show = () => globalIntercom('show');

/**
 * This will show the specified space in the Messenger, if that space is enabled.
 * @param spaceName
 */
export const showSpace = (
  spaceName: 'home' | 'messages' | 'help' | 'news' | 'tasks' | 'tickets',
) => globalIntercom('showSpace', spaceName);

/**
 * This will open the Messenger with the message list.
 */
export const showMessages = () => globalIntercom('showMessages');

/**
 * This will open the Messenger as if a new conversation was just created.
 *
 * This function can also take an optional parameter, used to pre-
 * populate the message composer
 * @param prePopulatedContent
 */
export const showNewMessage = (prePopulatedContent?: string) =>
  globalIntercom('showNewMessage', prePopulatedContent);

/**
 * You can submit an event using the trackEvent method. This will associate the
 * event with the currently logged in user and send it to Intercom.
 * @param eventName
 * @param metadata A map that can be used to send optional metadata about the event
 */
export const trackEvent = (
  eventName: string,
  metadata?: Record<string, string | number | boolean | Date>,
) => globalIntercom('trackEvent', eventName, metadata);

/**
 * A visitor is someone who goes to your site but does not use the messenger. You
 * can track these visitors via the visitor user_id. This user_id can be used to
 * retrieve the visitor or lead through the REST API.
 */
export const getVisitorId = () => globalIntercom('getVisitorId');

/**
 * If you would like to trigger a tour based on an action a user or visitor takes
 * in your site or application, you can use this API method. You need to call this
 * method with the id of the tour you wish to show. The id of the tour can be found in
 * the “Use tour everywhere” section of the tour editor.
 *
 * Please note that tours shown via this API must be published and the “Use tour
 * everywhere” section must be turned on. If you're calling this API using an invalid tour
 * id, nothing will happen. Nothing will happen if you call this API on mobile web as
 * tours do not work on mobile.
 * @param tourId
 */
export const startTour = (tourId: string) =>
  globalIntercom('startTour', tourId);

/**
 * If you would like to trigger an article in the Messenger, you can use the
 * showArticle method. The article will be shown within the Messenger, and clicking the
 * Messenger back button will return to the previous context.
 *
 * If the Messenger is closed when the method is called, it will be opened first
 * and then the article will be shown.
 * @param articleId
 */
export const showArticle = (articleId: string) =>
  globalIntercom('showArticle', articleId);

/**
 * If you would like to trigger a news item in the Messenger, you can use the
 * showNews method. The news item will be shown within the Messenger, and clicking the
 * Messenger back button will return to the previous context.
 *
 * If the Messenger is closed when the method is called, it will be opened first
 * and then the news item will be shown.
 * @param newsItemId
 */
export const showNews = (newsItemId: string) =>
  globalIntercom('showNews', newsItemId);

/**
 * If you would like to trigger a survey in the Messenger, you can use the
 * startSurvey method. The id of the survey can be found in the “Additional ways to share
 * your survey” section of the survey editor as well as in the URL of the editor.
 *
 * Please note that surveys shown via this API must be live. If you're calling
 * this API using an invalid survey id, nothing will happen.
 * @param surveyId
 */
export const startSurvey = (surveyId: string) =>
  globalIntercom('startSurvey', surveyId);

/**
 * If you would like to trigger a checklist in the Messenger, you can use the
 * startChecklist method. The id of the checklist can be found in the “Additional ways to share
 * your checklist” section of the checklist editor as well as in the URL of the
 * editor.
 *
 * Please note that checklists shown via this API must be live. If you're calling
 * this API using an invalid checklist id, nothing will happen.
 * @param checklistId
 */
export const startChecklist = (checklistId: string) =>
  globalIntercom('startChecklist', checklistId);

/**
 * If you would like to trigger a ticket in the Messenger, you can use the
 * showTicket method. The ticket will be shown within the Messenger, and clicking the
 * Messenger back button will return to the previous context.
 *
 * If the Messenger is closed when the method is called, it will be opened first
 * and then the ticket will be shown.
 * @param ticketId
 */
export const showTicket = (ticketId: string) =>
  globalIntercom('showTicket', ticketId);

/**
 * You can show a conversation programmatically in the Messenger by calling showConversation
 * method.
 * @param conversationId
 */
export const showConversation = (conversationId: string) =>
  globalIntercom('showConversation', conversationId);

/**
 * When we hide the messenger, you can hook into the event.
 * @param {Function} callback
 */
export const onHide = methods.onHide;

/**
 * When we show the messenger, you can hook into the event.
 * @param {Function} callback
 */
export const onShow = methods.onShow;

/**
 * This method allows you to register a function that will be called immediately
 * when invoked, and again whenever the current number of unread messages changes.
 * @param {Function} callback
 */
export const onUnreadCountChange = methods.onUnreadCountChange;

/**
 * When a visitor enters their email into the Messenger, you can hook into the event.
 * @param {Function} callback
 */
export const onUserEmailSupplied = methods.onUserEmailSupplied;
